import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { useIntl } from 'react-intl'
import { useIsElementVisible } from '@lib/hooks/is-element-visible'
import { UseCaseTab } from '../tab'
import { UseCaseLottiePlayer } from '../use-case-lottie-player'
import { useUseCaseTabsGnarTracking } from '@components/section/use-case/hooks/use-case-tabs-gnar-tracking'
import { AnySection } from '@lib/types/section'
import { isTypeAssetLottieInteractiveAnimation } from '@lib/types/content-types'
import styles from './use-case-tabs.module.scss'

export interface UseCaseTab {
  readonly title: string
  readonly content: AnySection
}

export interface UseCaseTabsProps {
  tabs: UseCaseTab[]
  className?: string
}

export const UseCaseTabs: React.FC<UseCaseTabsProps> = ({ tabs, className }) => {
  const intl = useIntl()
  const { isVisible, elementRef } = useIsElementVisible<HTMLElement>()
  const [activeTabIndex, _setActiveTabIndex] = useState(0)
  const gnarTracking = useUseCaseTabsGnarTracking()

  const setActiveTabIndex = (index: number) => {
    _setActiveTabIndex(index)
    gnarTracking.tabClick(index, tabs[index].title)
  }

  const [initialRender, setInitialRender] = useState(true)
  const tabListRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isVisible) {
      setInitialRender(false)
    }
  }, [isVisible])

  const openTab = (element: HTMLButtonElement, index: number) => {
    element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    setActiveTabIndex(index)
  }

  return (
    <section ref={elementRef} className={cn(styles.container, className)}>
      <div
        className={styles.tabList}
        role="tablist"
        aria-label={intl.formatMessage({
          id: 'useCase.tabs.tabList.ariaLabel',
          description: 'Use Case - Tabs - Tab List - Aria Label',
          defaultMessage: 'Grammarly Features',
        })}
        ref={tabListRef}
      >
        {tabs.map(({ title }, index) => (
          <UseCaseTab key={index} activeTabIndex={activeTabIndex} index={index} onTabClick={openTab}>
            {title}
          </UseCaseTab>
        ))}
      </div>

      {tabs.map((item, index) => {
        if (initialRender && activeTabIndex !== index) {
          return null
        }

        return (
          <div
            key={index}
            className={styles.panel}
            role="tabpanel"
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            aria-hidden={index !== activeTabIndex}
          >
            <section className={styles.content}>
              {isTypeAssetLottieInteractiveAnimation(item.content) ? (
                <UseCaseLottiePlayer
                  index={index}
                  structure={item.content}
                  activeTabIndex={activeTabIndex}
                  activeTabLabel={tabs[activeTabIndex].title}
                />
              ) : null}
            </section>
          </div>
        )
      })}
    </section>
  )
}
